import { useEffect, useRef, useState } from "react";
import { submitReservation, isFirebaseConfigured } from "@/lib/firebase";
import { SITE, whatsappLink } from "@/lib/site";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "",
  service: "",
  passengers: "1",
  pickup: "",
  destination: "",
  message: "",
};

const serviceLabels: Record<string, string> = {
  airport: "Airport Transfer",
  hotel: "Hotel Transfer",
  events: "Event / Occasion",
  nightout: "Nightlife",
  tours: "City Tour",
};

export default function ReservationSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState<"idle" | "success">("idle");
  // Link kept so the visitor can reopen WhatsApp if a popup blocker ate it.
  const [waFollowUp, setWaFollowUp] = useState<string | null>(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-6");
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // WhatsApp-formatted booking summary (asterisks render as bold in WhatsApp).
  const buildWhatsAppMessage = (d: typeof emptyForm) =>
    [
      `\u{1F697} *NEW RIDE REQUEST* | ${SITE.name}`,
      "",
      `\u{1F464} *Name:* ${d.name}`,
      `\u{1F4DE} *Phone:* ${d.phone}`,
      d.email ? `✉️ *Email:* ${d.email}` : "",
      `\u{1F698} *Service:* ${serviceLabels[d.service] ?? d.service}`,
      `\u{1F5D3} *Date:* ${d.date} at ${d.time}`,
      `\u{1F465} *Passengers:* ${d.passengers}`,
      `\u{1F4CD} *Pickup:* ${d.pickup}`,
      `\u{1F3C1} *Drop-off:* ${d.destination}`,
      d.message ? `\u{1F4DD} *Notes:* ${d.message}` : "",
    ]
      .filter(Boolean)
      .join("\n");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const waLink = whatsappLink(buildWhatsAppMessage(formData));
    // Open synchronously inside the click gesture so popup blockers allow it.
    window.open(waLink, "_blank");
    setWaFollowUp(waLink);
    if (isFirebaseConfigured) {
      // Silent backup copy in Firestore so no request is ever lost.
      submitReservation(formData).catch(() => {});
    }
    setStatus("success");
    setFormData(emptyForm);
  };

  return (
    <section
      id="reservation"
      className="py-14 md:py-20 lg:py-24 px-4 md:px-6 lg:px-10 bg-background-100"
    >
      <div
        ref={ref}
        className="max-w-7xl mx-auto opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block text-xs font-medium text-foreground-500 tracking-widest uppercase border border-foreground-200/60 px-3 py-1 rounded-full mb-4">
            Quick Reservation
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-tight mb-3">
            Book Your Los Angeles Ride
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
            Get a response within the hour. Whether it&rsquo;s an LAX airport
            transfer or a private city tour, we&rsquo;ll create a personalized
            experience just for you.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="lg:w-[45%] flex-shrink-0">
            <div className="bg-background-50 rounded-2xl border border-background-200/60 p-5 md:p-6">
              <form
                id="good-rides-reservation"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="res-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Full Name
                    </label>
                    <input
                      id="res-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="e.g. John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="res-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Email
                    </label>
                    <input
                      id="res-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="res-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Phone / WhatsApp
                    </label>
                    <input
                      id="res-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="+1 310 000 0000"
                    />
                  </div>
                  <div>
                    <label htmlFor="res-service" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Service Type
                    </label>
                    <select
                      id="res-service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    >
                      <option value="">Select</option>
                      <option value="airport">Airport Transfer (LAX / Burbank)</option>
                      <option value="hotel">Hotel Transfer</option>
                      <option value="events">Event / Occasion</option>
                      <option value="nightout">Nightlife</option>
                      <option value="tours">Private City Tour</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="res-date" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Date
                    </label>
                    <input
                      id="res-date"
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      min={today}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="res-time" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Time
                    </label>
                    <input
                      id="res-time"
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor="res-passengers" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Passengers
                    </label>
                    <select
                      id="res-passengers"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    >
                      {["1", "2", "3", "4", "5", "6", "7+"].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="res-pickup" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Pickup Location
                    </label>
                    <input
                      id="res-pickup"
                      type="text"
                      name="pickup"
                      value={formData.pickup}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="e.g. LAX Airport"
                    />
                  </div>
                  <div>
                    <label htmlFor="res-destination" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Drop-off Location
                    </label>
                    <input
                      id="res-destination"
                      type="text"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="e.g. Beverly Hills Hotel"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="res-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Additional Notes
                  </label>
                  <textarea
                    id="res-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                    placeholder="Flight number, child seat, luggage info..."
                  />
                  <p className="text-foreground-400 text-xs mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-fill text-lg" />
                  <span>Book Now on WhatsApp</span>
                </button>

                <p className="text-foreground-400 text-xs text-center">
                  Opens WhatsApp with your booking summary ready to send.
                </p>

                {status === "success" && (
                  <div className="px-3 py-3 rounded-lg bg-accent-100 text-accent-800 text-sm space-y-2.5">
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill" />
                      <span>
                        Your booking summary is ready in WhatsApp. Just tap
                        Send and we&rsquo;ll confirm within the hour.
                      </span>
                    </div>
                    {waFollowUp && (
                      <a
                        href={waFollowUp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-fill" />
                        <span>WhatsApp didn&rsquo;t open? Tap here</span>
                      </a>
                    )}
                    <p className="text-accent-800/80 text-xs">
                      Prefer to talk? Call{" "}
                      <a href={`tel:${SITE.phone}`} className="underline font-medium">
                        {SITE.phoneDisplay}
                      </a>
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="lg:w-[55%] flex flex-col gap-4">
            <div className="relative rounded-2xl overflow-hidden aspect-video lg:aspect-auto lg:h-64">
              <img
                src="/photos/hollywood-escalade.webp"
                alt="Private driver with a black Cadillac Escalade in front of the Hollywood sign in Los Angeles"
                loading="lazy"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-2">
                  <i className="ri-time-line text-lg" />
                </div>
                <p className="text-foreground-950 text-sm font-semibold">24/7 Service</p>
                <p className="text-foreground-500 text-xs mt-0.5">Late or early, always ready</p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-2">
                  <i className="ri-shield-check-line text-lg" />
                </div>
                <p className="text-foreground-950 text-sm font-semibold">Guaranteed</p>
                <p className="text-foreground-500 text-xs mt-0.5">Flight tracking &amp; delay handling</p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/60 p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-2">
                  <i className="ri-map-pin-line text-lg" />
                </div>
                <p className="text-foreground-950 text-sm font-semibold">Door to Door</p>
                <p className="text-foreground-500 text-xs mt-0.5">Luggage assistance included</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
